import classNames from 'classnames'
import { FC, PropsWithChildren } from 'react'

import { Link } from '@src/quince-components/src'
import { TrackConfig } from '@src/quince-components/src/hocs/withTracking'
import {
  getIndividualTagStatus,
  getProductTagsForUrgencyTagsForTracking,
  ProductTagKey,
} from '@src/quince-components/src/molecules/productCard/productCard.helpers'
import CUSTOM_SECTION_CONTAINER_TYPES from '@src/quince-site-common/src/constants/customSectionContainerTypes'
import useStorefrontModeContext from '@src/quince-site-common/src/contexts/storefront-mode-context'
import siteStateManager, { SearchDetails } from '@src/quince-site-common/src/redux/globalStateManager/SiteStateManager'
import { useAppSelector } from '@src/quince-site-common/src/redux/hooks'
import { handleAlgoliaClickEvent } from '@src/quince-site-common/src/services/algoliaService/algolia'
import { EDDDetails } from '@src/quince-site-common/src/services/eddService/eddService.types'
import { setFindingMethod } from '@src/telemetry/findingMethod'
import { getProductPropertiesForTrackingForCard } from '@src/telemetry/getProductPropertiesForTracking'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { transformTagsIntoAnalyticsTags } from '@src/utils/general.utils'

import { useProductCardHover } from '../../product-card-context/product-card-hover.context'
import { getProductLink, getProductLinkWithSelectedColor, getB2BProductPathname } from '../../product-card.helpers'
import { CardVariant, IWebsiteProduct, ProductCardReviewDetails } from '../../product-card.types'

import styles from './product-card-link.module.scss'

export interface ProductCardLinkProps {
  slug: string
  title: string
  spid: string | number
  colorVariants?: string[]
  selectedCardVariant?: CardVariant
  reviewSummary: ProductCardReviewDetails
  selectorKey?: string
  tracker?: string
  additionalAttributes?: Record<string, string>
  productCardLinkClass?: string
  siteSearchQuery?: string
  productPosition?: number
  productSearchObjectId?: string
  productClickTrackConfig?: TrackConfig
  eddDetails?: EDDDetails
  onClickCb?: () => void
  product: IWebsiteProduct
  allowCustomFooterHover?: boolean
  forceUnclickableHref?: boolean
}

// @i18n-ignore
const UNCLICKABLE_HREF = 'javascript:void(0)'

export const ProductCardLink: FC<PropsWithChildren<ProductCardLinkProps>> = ({
  slug,
  title,
  spid,
  selectedCardVariant,
  reviewSummary,
  selectorKey,
  tracker,
  additionalAttributes,
  productCardLinkClass,
  siteSearchQuery,
  productPosition,
  productSearchObjectId,
  productClickTrackConfig,
  colorVariants,
  children,
  eddDetails,
  onClickCb,
  product,
  allowCustomFooterHover = false,
  forceUnclickableHref = false,
}) => {
  const { setHovered } = useProductCardHover()
  const { isB2BMode, storeProgramResponse } = useStorefrontModeContext()

  const searchAlgoliaQueryId =
    useAppSelector<SearchDetails>(siteStateManager, siteStateManager.getSearchDetails).searchAlgoliaQueryId || ''

  const updatedSlug =
    isB2BMode && storeProgramResponse
      ? getB2BProductPathname(slug, storeProgramResponse.storeProgramDetails.slug)
      : slug

  let href = getProductLinkWithSelectedColor(updatedSlug, selectedCardVariant)

  const hrefOverride = getProductLink(
    updatedSlug,
    selectedCardVariant?.displayConfig.value,
    tracker,
    additionalAttributes,
    siteSearchQuery,
    productPosition,
  )
  href = hrefOverride || href

  // zero price should not be clickable
  if (!selectedCardVariant?.price?.salePrice || forceUnclickableHref) href = UNCLICKABLE_HREF
  return (
    <Link
      selectorKey={selectorKey}
      className={classNames(styles.productLink, productCardLinkClass)}
      href={href}
      enableEarlyFetch={false}
      onMouseEnter={() => {
        if (allowCustomFooterHover) {
          setHovered(true)
        }
      }}
      onMouseLeave={() => {
        if (allowCustomFooterHover) {
          setHovered(false)
        }
      }}
      onClick={() => {
        onClickCb?.()
        handleAlgoliaClickEvent({
          objectID: productSearchObjectId,
          position: productPosition,
          queryID: searchAlgoliaQueryId,
        })
        if (productClickTrackConfig) {
          let tags = selectedCardVariant?.tags.map(tag => tag.value as ProductTagKey) ?? []
          const urgencyTags = getProductTagsForUrgencyTagsForTracking(selectedCardVariant)
          tags = [...tags, ...urgencyTags]
          const productClickTrackConfigContext = productClickTrackConfig.context
          setFindingMethod[productClickTrackConfigContext?.findingMethod as string]?.({
            elementName: (productClickTrackConfigContext?.contentContainerName as string) || '',
            elementId: (productClickTrackConfigContext?.trackingId as string) || '',
          })
          trackEvent(TELEMETRY_EVENT_NAMES.PRODUCT_CLICK, {
            category: productClickTrackConfig.category,
            action: productClickTrackConfig.action,
            label: productClickTrackConfig.label,
            value: productClickTrackConfig.value,
            context: {
              ...productClickTrackConfig.context,
              product_id: spid,
              color: selectedCardVariant?.displayConfig.value,
              contentContainerType:
                productClickTrackConfig.context?.contentContainerType || CUSTOM_SECTION_CONTAINER_TYPES.PRODUCT_CARD,
              name: title,
              color_variant: colorVariants,
              count_color_variant: colorVariants?.length ?? 0,
              rating: reviewSummary.averageRating,
              product_position: productClickTrackConfig.context?.productPosition,
              tags: tags,
              tag_ids: transformTagsIntoAnalyticsTags(tags),
              ...getIndividualTagStatus(tags),
              productProperties: getProductPropertiesForTrackingForCard(
                product,
                tags,
                selectedCardVariant,
                productClickTrackConfig,
              ),
              edd_standard_eligible: eddDetails?.type === 'LB_STANDARD',
              edd_express_eligible: eddDetails?.type === 'LB_SUPER',
              edd_max_days: eddDetails?.max_days,
              edd_min_days: eddDetails?.min_days,
            },
          })
        }
      }}
    >
      {children}
    </Link>
  )
}
